import type { ProductEvent, ThreadSnapshot } from "@rakazo/contracts";
import {
  displayName,
  isBackgroundRunTrigger,
  plainTextFromMarkdown,
  REMOTE_TERMINATION_UNCONFIRMED_ERROR,
} from "@rakazo/core";
import { i18n } from "./i18n";

export type BrowserNotificationPermission = "default" | "denied" | "granted";

export type BrowserNotificationApi = {
  readonly permission: BrowserNotificationPermission;
  requestPermission(): Promise<BrowserNotificationPermission>;
};

let permissionRequest: Promise<BrowserNotificationPermission> | null = null;

export function requestBrowserNotificationPermission(
  api: BrowserNotificationApi | undefined = typeof Notification === "undefined"
    ? undefined
    : Notification,
): Promise<BrowserNotificationPermission> | undefined {
  if (!api) return undefined;
  if (api.permission !== "default") return Promise.resolve(api.permission);
  if (permissionRequest) return permissionRequest;
  try {
    permissionRequest = api.requestPermission().then(
      (permission) => {
        permissionRequest = null;
        return permission;
      },
      () => {
        permissionRequest = null;
        return api.permission;
      },
    );
  } catch {
    return Promise.resolve(api.permission);
  }
  return permissionRequest;
}

export type BrowserNotificationContext = {
  subscribedThreadId: string;
  initialCursor: number;
  streamReady: boolean;
  pageVisible: boolean;
  windowFocused: boolean;
  permission: BrowserNotificationPermission;
  notifiedEventIds: ReadonlySet<string>;
  runTrigger?: string;
};

/** Resolve the notification audience from durable run/receipt metadata, never prose.
 * Визначити адресата з метаданих запуску/отримання, а не зі змісту тексту. */
export function browserNotificationRunTrigger(
  snapshot: Pick<ThreadSnapshot, "run" | "activeRuns" | "messages"> | null,
  runId: string | null | undefined,
): string | undefined {
  if (!snapshot || !runId) return undefined;
  const run =
    snapshot.activeRuns?.find((run) => run.id === runId) ??
    (snapshot.run?.id === runId ? snapshot.run : undefined);
  if (run) return run.trigger;
  return snapshot.messages.some(
    (message) =>
      message.runId === runId &&
      message.blocks.some((block) => block.kind === "bot_message_received"),
  )
    ? "bot_message"
    : undefined;
}

function humanNotificationEvent(
  event: Pick<ProductEvent, "type" | "payload">,
  runTrigger?: string,
): boolean {
  const background =
    runTrigger === "bot_message" ||
    runTrigger === "created" ||
    (runTrigger !== undefined && isBackgroundRunTrigger(runTrigger));
  if (event.type !== "thread.message.created") return !background;
  if (event.payload.role !== "bot") return false;
  const blocks = Array.isArray(event.payload.blocks) ? event.payload.blocks : [];
  const peerReceipt = blocks.some(
    (block: unknown) =>
      block &&
      typeof block === "object" &&
      "kind" in block &&
      (block.kind === "bot_message_sent" || block.kind === "bot_message_received"),
  );
  return blocks.some((block: unknown) => {
    if (!block || typeof block !== "object" || !("kind" in block)) return false;
    if (["ask", "choice", "computer", "mcp_approval", "app_connect"].includes(String(block.kind)))
      return true;
    if (background) return false;
    if (block.kind === "image" || block.kind === "file") return !peerReceipt;
    return (
      block.kind === "text" &&
      "text" in block &&
      typeof block.text === "string" &&
      Boolean(block.text.trim())
    );
  });
}

export function shouldNotifyBrowser(
  event: Pick<ProductEvent, "id" | "type" | "threadId" | "seq" | "payload">,
  context: BrowserNotificationContext,
): boolean {
  const notifiable =
    (event.type === "thread.message.created" && event.payload.role === "bot") ||
    // A failure the system is going to retry is not worth a notification yet.
    (event.type === "run.failed" && typeof event.payload.retryAt !== "string") ||
    event.type === "run.cancelled";
  return (
    context.streamReady &&
    notifiable &&
    humanNotificationEvent(event, context.runTrigger) &&
    event.threadId === context.subscribedThreadId &&
    event.seq > context.initialCursor &&
    (!context.pageVisible || !context.windowFocused) &&
    context.permission === "granted" &&
    !context.notifiedEventIds.has(event.id)
  );
}

export function browserNotificationMessage(
  event: Pick<ProductEvent, "type" | "payload">,
  botName: string,
): { title: string; body: string } {
  const name = displayName(botName, i18n._({ id: "Bot", message: "Bot" }));
  if (event.type === "thread.message.created") {
    const blocks = Array.isArray(event.payload.blocks) ? event.payload.blocks : [];
    const body = blocks
      .map((block) =>
        block &&
        typeof block === "object" &&
        (block.kind === "text" || block.kind === "ask") &&
        "text" in block &&
        typeof block.text === "string"
          ? plainTextFromMarkdown(block.text)
          : "",
      )
      .filter(Boolean)
      .join("\n")
      .trim();
    return { title: name, body: body || i18n._({ id: "Message", message: "Message" }) };
  }
  if (event.type === "run.failed") {
    return {
      title: i18n._({ id: "{name} failed", message: "{name} failed", values: { name } }),
      body: i18n._({ id: "Failed.", message: "Failed." }),
    };
  }
  if (event.type === "run.cancelled") {
    return {
      title: i18n._({
        id: "{name}: agent stopped",
        message: "{name}: agent stopped",
        values: { name },
      }),
      body:
        event.payload?.terminationUnconfirmed === true
          ? REMOTE_TERMINATION_UNCONFIRMED_ERROR
          : i18n._({ id: "Agent dispatch stopped.", message: "Agent dispatch stopped." }),
    };
  }
  return {
    title: i18n._({ id: "{name} finished", message: "{name} finished", values: { name } }),
    body: i18n._({ id: "Finished.", message: "Finished." }),
  };
}

export function deliverBrowserNotification(
  event: Pick<ProductEvent, "id" | "type" | "threadId" | "payload">,
  botName: string,
  context: {
    enabled: boolean;
    pageVisible: boolean;
    windowFocused: boolean;
    permission: BrowserNotificationPermission;
    notifiedEventIds: Set<string>;
    runTrigger?: string;
    show: (
      title: string,
      body: string,
      tag: string,
    ) => Pick<Notification, "close" | "onclick"> | undefined;
    onOpen?: () => void;
  },
): "delivered" | "pending" | "discarded" {
  if (
    !context.enabled ||
    !humanNotificationEvent(event, context.runTrigger) ||
    (context.pageVisible && context.windowFocused) ||
    context.notifiedEventIds.has(event.id) ||
    context.permission === "denied"
  ) {
    return "discarded";
  }
  if (context.permission === "default") return "pending";
  const message = browserNotificationMessage(event, botName);
  try {
    const notification = context.show(message.title, message.body, event.threadId);
    if (notification && context.onOpen) {
      // Bind the originating conversation now, rather than the chat active at click time.
      // Зберігаємо початковий чат, а не чат, активний під час натискання.
      const open = context.onOpen;
      notification.onclick = () => {
        notification.close();
        open();
      };
    }
    context.notifiedEventIds.add(event.id);
    return "delivered";
  } catch {
    return "pending";
  }
}
